/**
 * FEAT-9: n8n Webhook Integration mit HMAC-Signatur
 *
 * Sendet Kontaktformular-Daten sicher an den n8n Webhook:
 * 1. HMAC-SHA256 Signatur (Web Crypto API, kein externes Package)
 * 2. Timestamp für Replay-Attack Prevention (max. 5 Minuten)
 * 3. Timeout nach 30 Sekunden
 */

interface ContactPayload {
  name: string
  email: string
  subject: string
  message: string
  turnstile_token: string // FEAT-10: Cloudflare Turnstile Token
  honeypot: string
  timestamp: number
}

interface WebhookResponse {
  success: boolean
  message?: string
  error?: string
}

class WebhookError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

async function generateHMAC(payload: object, secret: string): Promise<string> {
  const message = JSON.stringify(payload)
  const encoder = new TextEncoder()

  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )

  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(message))

  return Array.from(new Uint8Array(signature))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

export const useWebhook = () => {
  const config = useRuntimeConfig()

  const submitContact = async (formData: {
    name: string
    email: string
    subject: string
    message: string
    turnstile_token: string // FEAT-10
    honeypot: string
  }): Promise<WebhookResponse> => {
    const webhookUrl = config.public.n8nWebhookUrl
    const hmacSecret = config.public.hmacSecret

    if (!webhookUrl) {
      throw new WebhookError('Webhook-URL nicht konfiguriert. Bitte kontaktieren Sie mich direkt per E-Mail.', 500)
    }

    if (!hmacSecret) {
      throw new WebhookError('Konfigurationsfehler. Bitte kontaktieren Sie mich direkt per E-Mail.', 500)
    }

    const timestamp = Date.now()

    // Payload ohne Signatur – wird für HMAC verwendet
    // WICHTIG: Reihenfolge muss exakt der n8n-Implementierung entsprechen!
    // (name → email → subject → message → turnstile_token → honeypot → timestamp)
    const payload: ContactPayload = {
      name: formData.name,
      email: formData.email,
      subject: formData.subject,
      message: formData.message,
      turnstile_token: formData.turnstile_token, // FEAT-10
      honeypot: formData.honeypot,
      timestamp
    }

    const signature = await generateHMAC(payload, hmacSecret)

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30_000)

    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, signature }),
        signal: controller.signal
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new WebhookError(
          data.error || 'Serverfehler beim Senden der Nachricht.',
          response.status
        )
      }

      const data = await response.json().catch(() => ({ success: true }))
      return { success: true, message: data.message }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        throw new WebhookError('Zeitüberschreitung. Bitte versuchen Sie es später erneut.', 408)
      }
      throw err
    } finally {
      clearTimeout(timeoutId)
    }
  }

  return { submitContact }
}
